// One way to start a subscription checkout, shared by /pricing and the upsell
// prompts (#350).
//
// Extracted from PricingPage so the three upsell touchpoints don't each grow
// their own copy of the callable + redirect + error handling — and, more
// importantly, so `subscribe_checkout_started` is emitted from exactly one
// place. An event fired at three call sites is an event that will eventually be
// missing from one of them.

import { createSubscriptionCheckout } from '../firebase';
import { trackEvent } from '../analytics';
import { rememberUpsellSource, UPSELL_FLOWS } from '../upsell/attribution';

const DEFAULT_CHECKOUT_ERROR = 'We couldn’t start checkout. Please try again in a moment.';

// Messages we own, keyed by the callable's error code.
//
// `err.message` is NEVER surfaced directly: the Functions client wraps every
// failure in a FunctionsError whose `message` is populated even for
// transport-level ones, where it is the bare code ("internal", "unavailable",
// "deadline-exceeded"). A `(err.message || fallback)` therefore never reaches
// the fallback and renders "internal" at the user — and leaks server-thrown
// strings verbatim the rest of the time.
const FRIENDLY_BY_CODE = {
  'functions/failed-precondition':
    'You already have an active plan. Manage it from your account.',
  'functions/unauthenticated': 'Please sign in again to continue.',
  'functions/permission-denied': 'Please sign in again to continue.',
};

export function checkoutErrorMessage(err) {
  return FRIENDLY_BY_CODE[err && err.code] || DEFAULT_CHECKOUT_ERROR;
}

/**
 * Fire the funnel event, ask the server for a Stripe Checkout URL, and go there.
 *
 * Resolves only on failure (a success navigates away), returning an error
 * message the caller can render. Rejecting instead would make every call site
 * write the same try/catch.
 *
 * @param planId       the plan_id the server knows (tier.planId)
 * @param source       where the click came from — a UPSELL_PROMPTS id, or e.g.
 *                     `pricing_page`. Persisted across the Stripe redirect so
 *                     the activation event can be attributed to the touchpoint.
 * @param fromTier     the tier the account is on now (for upgrade/downgrade cuts)
 * @param successUrl   defaults to /account (where PlanBillingSection + the
 *                     activation events live)
 * @param cancelUrl    defaults to the current URL, so an abandoned checkout
 *                     returns the user to the surface they were working on
 * @param checkoutFn   injectable for tests
 * @param navigate     injectable for tests
 */
export async function startSubscriptionCheckout({
  planId,
  source,
  fromTier = 'free',
  successUrl,
  cancelUrl,
  checkoutFn = createSubscriptionCheckout,
  navigate = (url) => window.location.assign(url),
} = {}) {
  trackEvent('subscribe_checkout_started', {
    plan_id: planId,
    source,
    from_tier: fromTier,
  });
  // Written BEFORE the redirect: once Stripe takes over the tab, this session
  // is the only place that still knows which prompt sent them. Stashed under
  // the subscribe key so a top-up click can never be read back as a
  // subscription conversion.
  rememberUpsellSource(source, UPSELL_FLOWS.SUBSCRIBE);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const href = typeof window !== 'undefined' ? window.location.href : '';
  try {
    const { data } = await checkoutFn({
      plan_id: planId,
      success_url: successUrl || `${origin}/account`,
      cancel_url: cancelUrl || href,
    });
    if (data && data.url) {
      navigate(data.url);
      return { error: null };
    }
    throw new Error('No checkout URL returned.');
  } catch (err) {
    console.error('startSubscriptionCheckout failed', err);
    return { error: checkoutErrorMessage(err) };
  }
}

export default startSubscriptionCheckout;
